// React
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// React Query
import { useMutation, useQueryClient } from "@tanstack/react-query";

// Contexts
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";

// Components
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import MotivatorsSidebar from "@/Motivators/layouts/components/SideBars/MotivatorsSidebar";
import AutoAlertsSetupSection from "@Motivators/components/AutoAlerts/AutoAlertsSetupSection";
import { SpiffEntrySection, SpiffSetupSection } from "@Motivators/components/Spiffs";

import ShowInformation from "@/Layouts/components/Basics/ShowInformation";
import SpecialUnitsEntrySection from "@Motivators/components/SpecialUnits";
import TemporaryGuaranteedEntrySection from "@Motivators/components/TemporaryGuaranteed";
import TrainingSection from "@Motivators/components/Training/TrainingSection";
import { Grid } from "@mui/material";

// Helpers
import { createNewItem, getSetupByMotivatorType, newObjectSetup, unMaskId } from "@Motivators/helpers";
import { useSpecialUnitsInventoryFields } from "../components/SpecialUnits/Tables/hooks/useSpecialUnitsInventoryFields";

// Custom Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import useManualSpecialUnitsSubscription from "@Motivators/hooks/SpecialUnits/useManualSpecialUnitsSubscription";

// Constants
import { DATE_FORMAT } from "@/Constants/date";
import actionsList from "@Constants/actionsList";
import { ID_NEW_SETUP } from "../constants";
import { useLocationCycles } from "@/Washouts/hooks/common/useLocationCycles";

function useMotivatorsMutations(setupId, setOpenNewListDialog) {
	const { motivatorType, mutations, locationId, identifiers, setIsLoading, setSelection } = useMotivators();

	// Spiffs Setup
	const { mutate: createSpiffSetup } = useMutation({
		mutationKey: ["createSpiffSetup"],
		onMutate: () => {
			setIsLoading(true);
			setSelection((prev) => ({ ...prev, saving: true }));
		},
		mutationFn: ({ commissionCycleId, input }) =>
			mutations.createSetup({ locationId, input, commissionCycleId, department: identifiers.department }),
	});

	const { mutate: deleteSpiffSetup } = useMutation({
		mutationKey: ["deleteSpiffSetup"],
		mutationFn: (ccId) => mutations.deleteSetup({ locationId, setupId, commissionCycleId: ccId, department: identifiers.department }),
	});

	// Temporary Guaranteed Setup
	const { mutate: createTemporaryGuaranteedSetup } = useMutation({
		mutationKey: ["createTemporaryGuaranteedSetup"],
		onMutate: () => {
			setIsLoading(true);
			setSelection((prev) => ({ ...prev, saving: true }));
		},
		mutationFn: ({ commissionCycleId, input }) =>
			mutations.createSetup({ locationId, commissionCycleId, input, department: identifiers.department }),
	});

	const { mutate: deleteTemporaryGuaranteedSetup } = useMutation({
		mutationKey: ["deleteTemporaryGuaranteedSetup"],
		mutationFn: (ccId) => mutations.deleteSetup({ locationId, setupId, commissionCycleId: ccId, department: identifiers.department }),
	});

	// Auto Alerts
	const { mutate: createAutoAlertsSetup } = useMutation({
		mutationKey: ["createAutoAlertsSetup"],
		mutationFn: ({ commissionCycleId, input }) => mutations.createSetup({ locationId, commissionCycleId, input }),
		onMutate: () => {
			setIsLoading(true);
			setSelection((prev) => ({ ...prev, saving: true }));
		},
	});

	const { mutate: deleteAutoAlertsSetup } = useMutation({
		mutationKey: ["deleteAutoAlertsSetup"],
		mutationFn: (ccId) => {
			const id = setupId?.includes("_") ? setupId.split("_")[1] : setupId;
			return mutations.deleteSetup({ locationId, setupId: id, commissionCycleId: ccId });
		},
	});

	// Special Units.
	const createSpecialUnitsSetup = () => setOpenNewListDialog(true);

	const { mutate: deleteSpecialUnitsSetup } = useMutation({
		mutationKey: ["deleteSpecialUnitsSetup"],
		mutationFn: (ccId) => {
			const id = setupId?.includes("_") ? setupId.split("_")[1] : setupId;
			return mutations.deleteSetup({ locationId, setupId: id, commissionCycleId: ccId });
		},
	});

	switch (motivatorType) {
		case "spiffSetup":
		case "spiffBonusSetup":
			return {
				createSetup: createSpiffSetup,
				deleteSetup: deleteSpiffSetup,
			};
		case "salesTemporaryGuaranteed":
		case "temporaryGuaranteed":
			return {
				createSetup: createTemporaryGuaranteedSetup,
				deleteSetup: deleteTemporaryGuaranteedSetup,
			};
		case "autoAlertsSetup":
			return {
				createSetup: createAutoAlertsSetup,
				deleteSetup: deleteAutoAlertsSetup,
			};
		case "specialUnitsSetup":
			return {
				createSetup: createSpecialUnitsSetup,
				deleteSetup: deleteSpecialUnitsSetup,
			};
		default:
			return {
				createSetup: () => {},
				deleteSetup: () => {},
			};
	}
}

const getNewSetupId = (commissionCycleId) => {
	return `${commissionCycleId}_${ID_NEW_SETUP}_${Math.floor(Math.random() * 1000)}`;
}

export default function MotivatorsWrapper() {
	const [openNewListDialog, setOpenNewListDialog] = useState(false);
	const [commissionCId, setCommissionCId] = useState("");
	const [commissionCycleStartDate, setCommissionCycleStartDate] = useState("");
	const [commissionCycleEndDate, setCommissionCycleEndDate] = useState("");
	const [sidebarOpen, setSidebarOpen] = useState(true);
	// Local State
	const lastSelected = useRef(null);

	// Hooks
	const { locationId, motivatorType, subscriptions, selection, setSelection, identifiers, isLoading, setSelectNewlyCreated } =
		useMotivators();
	const { addSubscription, deleteSubscription } = useSubscriptions();
	const { data: commissionCycles } = useLocationCycles();

	// Preload Inventory Fields when on the Special Units Setup page
	useSpecialUnitsInventoryFields(motivatorType === "specialUnitsSetup");

	// Permission Special Editing on Motivators
	const { useAuth } = useAuthorization();
	const MASTER_EDIT_MOTIVATORS = {
		SALES: useAuth(actionsList.MASTER_EDIT_SALES_MOTIVATORS),
		SERVICE: useAuth(actionsList.MASTER_EDIT_SERVICE_MOTIVATORS),
	};

	const queryClient = useQueryClient();

	// Subscribe to manual‑special‑units updates for this location
	useManualSpecialUnitsSubscription(locationId);

	/************************************************** SIDEBAR MUTATIONS **************************************************/
	const { createSetup, deleteSetup } = useMotivatorsMutations(selection?._id, setOpenNewListDialog);

	/*************************************************** HANDLERS ***************************************************/
	const handleAddSetup = ({ commissionCycleId, commissionCycleStartDate, commissionCycleEndDate }) => {
		switch (motivatorType) {
			case "specialUnitsSetup":
				setOpenNewListDialog(true);
				setCommissionCId(commissionCycleId);
				setCommissionCycleEndDate(commissionCycleEndDate);
				setCommissionCycleStartDate(commissionCycleStartDate);
				break;

			case "spiffSetup":
			case "spiffBonusSetup": {
				const sidebarData = queryClient.getQueryData([`${motivatorType}Sidebar`, locationId]);
				const commissionCycle = commissionCycles?.find((cc) => cc._id === commissionCycleId);

				const newSetupId = getNewSetupId(commissionCycleId);
				const newSetup = {
					...newObjectSetup(
						identifiers.title,
						motivatorType,
						commissionCycleId,
						commissionCycle?.startDate,
						commissionCycle?.endDate
					),
					itemCount: 0,
					list: [],
					_id: newSetupId,
				};

				const newTree =
					sidebarData.tree.map((year) => ({
						...year,
						list: year.list.map((month) => {
							if (month._id === commissionCycleId) {
								return {
									...month,
									list: [newSetup, ...(month.list || [])],
								};
							}
							return month;
						}),
					})) || [];

				queryClient.setQueryData([`${motivatorType}Sidebar`, locationId], {
					...sidebarData,
					tree: newTree,
				});

				if (lastSelected.current?._id !== newSetupId) {
					lastSelected.current = newSetup;
					setSelection(newSetup);
				}

				setSelectNewlyCreated(true);
				break;
			}

			default: {
				if (!locationId) {
					return;
				}

				const data = queryClient.getQueryData([`${motivatorType}Sidebar`, locationId]);
				if (data) {
					const newSetup = newObjectSetup(
						identifiers.title,
						motivatorType,
						commissionCycleId,
						commissionCycleStartDate,
						commissionCycleEndDate
					);
					const newData = createNewItem({
						tree: data,
						ccId: commissionCycleId,
						newItem: newSetup,
						param: identifiers.attribute,
					});

					queryClient.setQueryData([`${motivatorType}Sidebar`, locationId], newData);

					const newSetupGeneric = getSetupByMotivatorType(motivatorType, newSetup);
					if (lastSelected.current?._id !== newSetupGeneric?._id) {
						const newSetupSelected = { ...newSetupGeneric, _id: `null_${newSetupGeneric._id}` };
						lastSelected.current = newSetupSelected;
						setSelection(newSetupSelected);
					}
				}
				setSelectNewlyCreated(true);
				break;
			}
		}
	};

	const subscribeToBody = (params, subscriptionsObj, subscriptionKey) => {
		const filteredSubscription = Array.from(subscriptionsObj.entries()).filter(([_, eventQuery]) => eventQuery.query);
		const resolvedSubscriptions = filteredSubscription.map(([event, eventQuery]) => {
			return {
				context: event,
				query: eventQuery.query,
				variables: {
					locationId: params?.locationId,
					_id: params?._id,
					commissionCycleId: params?.commissionCycleId,
				},
			};
		});

		addSubscription([subscriptionKey, params?.locationId, params?._id], resolvedSubscriptions);
	};

	const sideSelection = useCallback(
		(selected) => {
			if (lastSelected.current?._id !== selected?._id) {
				lastSelected.current = selected;

				if (selected?.controlled) {
					setSelectNewlyCreated(true);
				}

				const selectedId = unMaskId(selected?._id);
				if (!selected?.controlled) {
					deleteSubscription([motivatorType + "Body", locationId]);
					subscribeToBody(
						{ locationId, department: identifiers.department, _id: selectedId, commissionCycleId: selected?.commissionCycleId },
						subscriptions,
						motivatorType + "Body"
					);
				}
			}
			setSelection(selected);
		},
		[motivatorType, locationId, identifiers.department, setSelection, setSelectNewlyCreated, deleteSubscription, subscriptions]
	);

	const sideBar = useMemo(() => {
		return (
			<CollapsibleLeftPanel
				loading={isLoading}
				open={true}
				permanentTrigger={sidebarOpen}
				control={
					<MotivatorsSidebar
						selected={selection}
						callbackSelected={sideSelection}
						callbackAdd={handleAddSetup}
						callbackDelete={deleteSetup}
						lastSelected={lastSelected}
						setOpenNewListDialog={setOpenNewListDialog}
						masterEdit={MASTER_EDIT_MOTIVATORS}
					/>
				}
			/>
		);
	}, [selection, sideSelection, handleAddSetup, deleteSetup, setOpenNewListDialog, MASTER_EDIT_MOTIVATORS, isLoading, sidebarOpen]);

	useEffect(() => {
		return () => {
			lastSelected.current = null;
			setSelection(null);
		};
	}, [locationId]);

	const componentMap = {
		spiffAwards: {
			component: SpiffEntrySection,
			props: { selectedItem: selection },
		},
		spiffBonusAwards: {
			component: SpiffEntrySection,
			props: { selectedItem: selection },
		},
		autoAlertsSetup: {
			component: AutoAlertsSetupSection,
			props: {
				createSetup,
				selectedItem: selection,
				hasSpecialEditing: MASTER_EDIT_MOTIVATORS.SALES,
			},
		},
		spiffSetup: {
			component: SpiffSetupSection,
			props: {
				createSetup,
				selectedItem: selection,
				setSelectedItem: setSelection,
				hasSpecialEditing: MASTER_EDIT_MOTIVATORS.SALES,
			},
		},
		spiffBonusSetup: {
			component: SpiffSetupSection,
			props: {
				createSetup,
				selectedItem: selection,
				setSelectedItem: setSelection,
				hasSpecialEditing: MASTER_EDIT_MOTIVATORS.SERVICE,
			},
		},
		spiffSetupAwards: {
			component: SpiffSetupSection,
			props: { selectedItem: selection },
		},
		serviceTraining: {
			component: TrainingSection,
			props: {
				selectedItem: selection,
				setSelectedItem: setSelection,
			},
		},
		salesTemporaryGuaranteed: {
			component: TemporaryGuaranteedEntrySection,
			props: {
				createSetup,
				selectedItem: selection,
				setSelectedItem: setSelection,
				hasSpecialEditing: MASTER_EDIT_MOTIVATORS.SALES,
			},
		},
		temporaryGuaranteed: {
			component: TemporaryGuaranteedEntrySection,
			props: {
				createSetup,
				selectedItem: selection,
				setSelectedItem: setSelection,
				hasSpecialEditing: MASTER_EDIT_MOTIVATORS.SERVICE,
			},
		},
		specialUnitsSetup: {
			component: SpecialUnitsEntrySection,
			props: {
				openNewListDialog,
				setOpenNewListDialog,
				commissionCycleStartDate,
				commissionCycleEndDate,
				lastSelected,
				selectedItem: selection,
				setSelectedItem: setSelection,
				commissionCycleId: commissionCId,
				hasSpecialEditing: MASTER_EDIT_MOTIVATORS.SALES,
			},
		},
	};

	const getAwardComponent = () => {
		const { component: Component, props: getProps } = componentMap[motivatorType] || {};
		if (!Component) {
			throw new Error("Invalid motivator type. Cannot render component.");
		}
		return <Component {...getProps} />;
	};

	MotivatorsWrapper.propTypes = {
		selection: PropTypes.object.isRequired,
		setSelection: PropTypes.func.isRequired,
		createSetup: PropTypes.func.isRequired,
		MASTER_EDIT_MOTIVATORS: PropTypes.object.isRequired,
		openNewListDialog: PropTypes.bool,
		setOpenNewListDialog: PropTypes.func,
		commissionCId: PropTypes.string,
		commissionCycleStartDate: PropTypes.string,
		commissionCycleEndDate: PropTypes.string,
		lastSelected: PropTypes.object,
		motivatorType: PropTypes.string.isRequired,
	};

	return (
		<>
			<BodyLayer floatingContent={sideBar}>{getAwardComponent()}</BodyLayer>
			{identifiers.createdBy && (
				<Grid className="ml-[270px] mb-[15px] mt--2 relative z-10">
					{
						<ShowInformation
							createdBy={selection?.[identifiers.createdBy]?.fullName || ""}
							createdAt={selection?.[identifiers.createdOn] || ""}
							updatedBy={selection?.[identifiers.lastModifiedBy]?.fullName || ""}
							updatedAt={String(selection?.[identifiers.lastModifiedOn] || "")}
							show={true}
							format={DATE_FORMAT.fullAbbrTime12hPadded}
							loading={isLoading}
						/>
					}
				</Grid>
			)}
		</>
	);
}
